import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getTransactionDetail, 
  updateTransactionStatus 
} from '../../services/api';
import { 
  FiArrowLeft, 
  FiRefreshCw, 
  FiUser, 
  FiCreditCard, 
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiDownload,
  FiCopy,
  FiChevronRight,
  FiCalendar,
  FiMail,
  FiPhone,
  FiFileText,
  FiShield
} from 'react-icons/fi';
import { 
  TbArrowsExchange,
  TbReceiptRefund
} from 'react-icons/tb';

function TransactionDetail() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [metadata, setMetadata] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState(null);

  useEffect(() => {
    fetchTransactionDetail();
  }, [transactionId]);

  useEffect(() => {
    if (transaction?.metadata) {
      try {
        const parsed = JSON.parse(transaction.metadata);
        setMetadata(parsed);
      } catch (err) {
        console.error('Error parsing metadata:', err);
      }
    }

    if (transaction?.fee_breakdown) {
      try {
        const parsed = JSON.parse(transaction.fee_breakdown);
        setFeeBreakdown(parsed);
      } catch (err) {
        console.error('Error parsing fee breakdown:', err);
      }
    }
  }, [transaction]);

  const fetchTransactionDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getTransactionDetail(transactionId);
      
      if (response.success) {
        setTransaction(response.data);
      } else {
        setError(response.message || 'Failed to load transaction details');
      }
    } catch (err) {
      setError('Failed to load transaction details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to change transaction status to "${newStatus}"?`)) {
      return;
    }

    try {
      const adminNotes = prompt('Enter admin notes (optional):', '');
      const response = await updateTransactionStatus(transactionId, newStatus, adminNotes || '');

      if (response.success) {
        setSuccessMessage(`Transaction status updated to ${newStatus} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchTransactionDetail(); // Refresh details
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError(`Failed to update transaction status: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-4 py-2 text-sm font-medium rounded-full flex items-center justify-center";
    
    const statusConfig = {
      'successful': { bg: 'bg-green-100', text: 'text-green-800', label: 'Successful', icon: FiCheckCircle },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending', icon: FiClock },
      'failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed', icon: FiXCircle },
      'cancelled': { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Cancelled', icon: FiXCircle },
      'hold': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'On Hold', icon: FiAlertCircle },
      'refunded': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Refunded', icon: TbReceiptRefund },
      'reversed': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Reversed', icon: TbArrowsExchange }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: FiAlertCircle };
    const Icon = config.icon;
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        <Icon className="mr-2" />
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (type === 'credit') {
      return (
        <span className="px-4 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-full border border-green-200 flex items-center">
          <span className="mr-2 text-lg">↑</span> Credit (Incoming)
        </span>
      );
    }
    return (
      <span className="px-4 py-2 bg-red-50 text-red-700 text-sm font-medium rounded-full border border-red-200 flex items-center">
        <span className="mr-2 text-lg">↓</span> Debit (Outgoing)
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getTransactionTypeLabel = (type) => {
    const types = {
      'withdrawal': 'Withdrawal',
      'deposit': 'Deposit',
      'payment': 'Payment',
      'refund': 'Refund',
      'fee': 'Fee',
      'bonus': 'Bonus'
    };
    return types[type] || type.replace('_', ' ');
  };

  const getWalletStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full";
    
    if (status === 'active') {
      return <span className={`${baseClasses} bg-green-100 text-green-800`}>Active</span>;
    } else if (status === 'frozen') {
      return <span className={`${baseClasses} bg-red-100 text-red-800`}>Frozen</span>;
    } else if (status === 'suspended') {
      return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Suspended</span>;
    }
    return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="flex items-center mb-8">
            <div className="h-10 w-10 bg-gray-200 rounded-lg mr-4"></div>
            <div>
              <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
          </div>

          {/* Main Card Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-100 rounded w-32 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-48"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="h-8 bg-gray-100 rounded w-48 mb-6"></div>
            <div className="h-40 bg-gray-50 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/finance/transactions')}
            className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
          >
            <FiArrowLeft className="mr-2" />
            Back
          </button>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 text-center">
          <FiAlertCircle className="text-red-500 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Transaction</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => navigate('/admin/finance/transactions')}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
            >
              Back to Transactions
            </button>
            <button
              onClick={fetchTransactionDetail}
              className="px-6 py-3 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="text-center py-16">
          <div className="text-gray-300 text-8xl mb-6">💳</div>
          <h3 className="text-xl font-medium text-gray-700 mb-2">Transaction Not Found</h3>
          <p className="text-gray-500 mb-6">The transaction you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/finance/transactions')}
            className="px-6 py-3 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] font-medium"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center mb-4">
              <button
                onClick={() => navigate('/admin/finance/transactions')}
                className="flex items-center text-gray-600 hover:text-gray-800 mr-4 transition-colors"
              >
                <FiArrowLeft className="mr-2" />
                Back to Transactions
              </button>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Transaction Details</h1>
              <div className="flex items-center space-x-2">
                {getStatusBadge(transaction.status)}
                {getTypeBadge(transaction.type)}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600">
              <div className="flex items-center">
                <FiCreditCard className="mr-2" />
                <span className="font-mono font-medium">{transaction.reference}</span>
                <button
                  onClick={() => copyToClipboard(transaction.reference)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                  title="Copy Reference"
                >
                  <FiCopy />
                </button>
              </div>
              <div className="flex items-center">
                <FiCalendar className="mr-2" />
                <span>{formatDate(transaction.created_at)}</span>
              </div>
              <div className="flex items-center">
                <span className="bg-gray-100 px-3 py-1 rounded-lg text-sm">
                  ID: {transaction.transaction_id}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-3">
            <button
              onClick={fetchTransactionDetail}
              className="px-4 py-2.5 bg-white text-[#0f0e0e] border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
            <button
              onClick={() => {/* Export functionality */}}
              className="px-4 py-2.5 bg-white text-[#0f0e0e] border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center"
            >
              <FiDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column - Summary */}
        <div className="lg:col-span-2 space-y-6">
          {/* Transaction Summary Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center">
                <FiDollarSign className="mr-2 text-[#ff660e]" />
                Transaction Summary
              </h2>
              <div className={`text-3xl font-bold ${
                transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
              }`}>
                {transaction.type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Transaction Type</label>
                  <p className="text-lg font-medium text-[#0f0e0e] capitalize">
                    {getTransactionTypeLabel(transaction.transaction_type)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                  <p className="text-[#0f0e0e]">{transaction.description}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
                  <div className="flex items-center text-gray-600">
                    <FiCalendar className="mr-2" />
                    <span>{formatDateTime(transaction.created_at)}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                  <div className="flex items-center text-gray-600">
                    <FiClock className="mr-2" />
                    <span>{formatDateTime(transaction.updated_at)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Net Amount</label>
                  <p className="text-2xl font-bold text-[#0f0e0e]">
                    {formatCurrency(transaction.net_amount)}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Platform Fee:</span>
                    <span className="font-medium">{formatCurrency(transaction.platform_fee)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Processor Fee:</span>
                    <span className="font-medium">{formatCurrency(transaction.processor_fee)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Fees:</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(parseFloat(transaction.platform_fee) + parseFloat(transaction.processor_fee))}
                    </span>
                  </div>
                </div>
                
                {feeBreakdown && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{feeBreakdown.summary}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* User Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiUser className="mr-2 text-[#ff660e]" />
              User Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">User</label>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#ff660e] to-orange-400 flex items-center justify-center text-white font-bold mr-3">
                      {transaction.first_name?.charAt(0)}{transaction.last_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-[#0f0e0e]">
                        {transaction.first_name} {transaction.last_name}
                      </p>
                      <p className="text-sm text-gray-500">ID: {transaction.user_id}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <div className="flex items-center text-gray-600">
                    <FiMail className="mr-2" />
                    <span>{transaction.user_email}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                  <div className="flex items-center text-gray-600">
                    <FiPhone className="mr-2" />
                    <span>{transaction.phone_number || 'N/A'}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Current Balance</label>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiCreditCard className="mr-2 text-gray-400" />
                      <span className="text-xl font-bold text-[#0f0e0e]">
                        {formatCurrency(transaction.current_balance)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">Wallet ID:</span>
                      <span className="font-medium">{transaction.wallet_id}</span>
                      {getWalletStatusBadge(transaction.wallet_status)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate(`/admin/finance/wallets/${transaction.wallet_id}`)}
                className="text-[#ff660e] hover:text-[#e55c0d] font-medium flex items-center"
              >
                View Wallet Details
                <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Actions & Info */}
        <div className="space-y-6">
          {/* Status Actions Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiShield className="mr-2 text-[#ff660e]" />
              Status Management
            </h2>

            <div className="space-y-3">
              {transaction.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleStatusUpdate('successful')}
                    className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium flex items-center justify-center border border-green-200 transition-colors"
                  >
                    <FiCheckCircle className="mr-2" />
                    Approve Transaction
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('failed')}
                    className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium flex items-center justify-center border border-red-200 transition-colors"
                  >
                    <FiXCircle className="mr-2" />
                    Reject Transaction
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('hold')}
                    className="w-full px-4 py-3 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg font-medium flex items-center justify-center border border-orange-200 transition-colors"
                  >
                    <FiAlertCircle className="mr-2" />
                    Place on Hold
                  </button>
                </>
              )}

              {transaction.status === 'successful' && (
                <>
                  <button
                    onClick={() => handleStatusUpdate('reversed')}
                    className="w-full px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-medium flex items-center justify-center border border-purple-200 transition-colors"
                  >
                    <TbArrowsExchange className="mr-2" />
                    Reverse Transaction
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('refunded')}
                    className="w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium flex items-center justify-center border border-blue-200 transition-colors"
                  >
                    <TbReceiptRefund className="mr-2" />
                    Issue Refund
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('hold')}
                    className="w-full px-4 py-3 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg font-medium flex items-center justify-center border border-orange-200 transition-colors"
                  >
                    <FiAlertCircle className="mr-2" />
                    Place on Hold
                  </button>
                </>
              )}

              {transaction.status === 'hold' && (
                <>
                  <button
                    onClick={() => handleStatusUpdate('successful')}
                    className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium flex items-center justify-center border border-green-200 transition-colors"
                  >
                    <FiCheckCircle className="mr-2" />
                    Release from Hold
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('failed')}
                    className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium flex items-center justify-center border border-red-200 transition-colors"
                  >
                    <FiXCircle className="mr-2" />
                    Cancel Transaction
                  </button>
                </>
              )}

              {['failed', 'cancelled'].includes(transaction.status) && (
                <button
                  onClick={() => handleStatusUpdate('successful')}
                  className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium flex items-center justify-center border border-green-200 transition-colors"
                >
                  <FiCheckCircle className="mr-2" />
                  Mark as Successful
                </button>
              )}

              <div className="pt-4 mt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Current Status</p>
                <div className="flex justify-center">
                  {getStatusBadge(transaction.status)}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiFileText className="mr-2 text-[#ff660e]" />
              Quick Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Transaction ID</label>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-gray-600">{transaction.transaction_id}</span>
                  <button
                    onClick={() => copyToClipboard(transaction.transaction_id)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Copy ID"
                  >
                    <FiCopy />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Wallet ID</label>
                <span className="font-medium text-gray-600">{transaction.wallet_id}</span>
              </div>

              {transaction.order_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Order ID</label>
                  <span className="font-medium text-gray-600">{transaction.order_id}</span>
                </div>
              )}

              {metadata?.payment_reference && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Payment Reference</label>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-600 truncate">{metadata.payment_reference}</span>
                    <button
                      onClick={() => copyToClipboard(metadata.payment_reference)}
                      className="text-gray-400 hover:text-gray-600 ml-2"
                      title="Copy Reference"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Metadata Tab */}
      {metadata && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center">
              <FiFileText className="mr-2 text-[#ff660e]" />
              Metadata & Additional Information
            </h2>
            <button
              onClick={() => copyToClipboard(JSON.stringify(metadata, null, 2))}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium flex items-center"
            >
              <FiCopy className="mr-2" />
              Copy JSON
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          </div>

          {/* Display specific metadata fields */}
          {metadata.status_updated_by && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">Status Update History</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">Updated by Admin ID:</span>
                  <span className="font-medium">{metadata.status_updated_by}</span>
                </div>
                {metadata.status_updated_at && (
                  <div className="flex justify-between">
                    <span className="text-blue-700">Updated at:</span>
                    <span className="font-medium">{formatDateTime(metadata.status_updated_at)}</span>
                  </div>
                )}
                {metadata.admin_notes && (
                  <div>
                    <span className="text-blue-700">Admin Notes:</span>
                    <p className="mt-1 text-blue-800">{metadata.admin_notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default TransactionDetail;